import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { viewYoutubeService, ViewYoutube } from '../../../services/viewYoutubeService';
import { settingsService } from '../../../services/settingsService';
import { useToast } from '../../../components/Toast';
import { useAuth } from '../../../hooks/useAuth';

export const useCheckView = () => {
    const { t } = useTranslation('common');
    const { showToast } = useToast();
    const { user } = useAuth();

    const [youtubeUrl, setYoutubeUrl] = useState('');
    const [checkViewLoading, setCheckViewLoading] = useState(false);
    const [videoData, setVideoData] = useState<ViewYoutube | null>(null);

    const [videos, setVideos] = useState<ViewYoutube[]>([]);
    const [loading, setLoading] = useState(false);
    const [search, setSearch] = useState('');
    const [channelFilter, setChannelFilter] = useState('');
    const [channelOptions, setChannelOptions] = useState<string[]>([]);
    const [dateStart, setDateStart] = useState('');
    const [dateEnd, setDateEnd] = useState('');
    const [statusFilter, setStatusFilter] = useState<string>('');
    const [page, setPage] = useState(1);
    const [total, setTotal] = useState(0);
    const [perPage] = useState(20);

    const [checkViewEnabled, setCheckViewEnabled] = useState(true);
    const [loadingSetting, setLoadingSetting] = useState(true);

    // Load check view setting
    useEffect(() => {
        const loadSetting = async () => {
            try {
                const enabled = await settingsService.isCheckViewEnabled();
                setCheckViewEnabled(enabled);
            } catch (error) {
                console.error('Error loading check view setting:', error);
                setCheckViewEnabled(true);
            } finally {
                setLoadingSetting(false);
            }
        };
        loadSetting();
    }, []);

    // Initialize dates
    useEffect(() => {
        if (!checkViewEnabled) return;
        const end = new Date();
        const start = new Date();
        start.setDate(start.getDate() - 30);
        setDateEnd(end.toISOString().split('T')[0]);
        setDateStart(start.toISOString().split('T')[0]);
    }, [checkViewEnabled]);

    const fetchVideos = useCallback(async () => {
        if (!user || !user.id || !checkViewEnabled) return;

        setLoading(true);
        try {
            const data = await viewYoutubeService.getByDateRange(dateStart, dateEnd);

            // Filter only user's videos
            const userId = Number(user.id);
            let filtered = data.filter(v => {
                if (!v.created_by) return false;
                const videoCreatedBy = Number(v.created_by);
                return videoCreatedBy === userId;
            });

            // Update channel options
            const uniqueChannels = Array.from(
                new Set(
                    filtered
                        .map((v) => v.NameChannel?.trim())
                        .filter((name): name is string => !!name)
                )
            ).sort((a, b) => a.localeCompare(b));
            setChannelOptions(uniqueChannels);

            // Apply Filter by status
            if (statusFilter) {
                filtered = filtered.filter(v => v.status === statusFilter);
            }

            // Apply Filter by channel
            if (channelFilter) {
                const keyword = channelFilter.toLowerCase();
                filtered = filtered.filter(v =>
                    (v.NameChannel || '').toLowerCase().includes(keyword)
                );
            }

            // Apply search
            if (search) {
                const term = search.toLowerCase();
                filtered = filtered.filter(v =>
                    v.Content?.toLowerCase().includes(term) ||
                    v.NameChannel?.toLowerCase().includes(term) ||
                    v.Linkvideo?.toLowerCase().includes(term)
                );
            }

            // Pagination
            const startIndex = (page - 1) * perPage;
            const endIndex = startIndex + perPage;
            const paginated = filtered.slice(startIndex, endIndex);

            setVideos(paginated);
            setTotal(filtered.length);
        } catch (error: any) {
            showToast(t('checkView.messages.error'), 'error');
        } finally {
            setLoading(false);
        }
    }, [user, checkViewEnabled, dateStart, dateEnd, statusFilter, channelFilter, search, page, perPage, showToast, t]);

    useEffect(() => {
        if (dateStart && dateEnd) {
            fetchVideos();
        }
    }, [fetchVideos]);

    const handleCheckView = async (e: React.FormEvent) => {
        e.preventDefault();

        if (!youtubeUrl.trim()) {
            showToast(t('checkView.messages.enterUrl'), 'warning');
            return;
        }

        setCheckViewLoading(true);
        setVideoData(null);

        try {
            const video = await viewYoutubeService.upload({
                Content: '',
                Country: 'Vietnam',
                View: 0,
                NameChannel: '',
                Uploader: '',
                Linkvideo: youtubeUrl.trim(),
                Datetimeuploading: new Date().toISOString(),
                status: 'active',
            });

            setVideoData(video);
            setYoutubeUrl('');

            // Delay slightly check result to user
            setTimeout(async () => {
                await fetchVideos();

                // Try to find the latest video to show specific toast
                if (video.View && video.View > 0) {
                    showToast(t('checkView.messages.addedCheckSuccess', { views: video.View.toLocaleString() }), 'success');
                } else {
                    // simplified message
                    showToast(t('checkView.messages.addedSuccess'), 'success');
                }
            }, 500);

        } catch (error: any) {
            const errorMessage = error.response?.data?.error || t('checkView.messages.error');
            showToast(errorMessage, 'error');
        } finally {
            setCheckViewLoading(false);
        }
    };

    const deleteVideo = async (id: number) => {
        if (!window.confirm(t('checkView.messages.deleteConfirm'))) return;
        try {
            await viewYoutubeService.delete(id);
            showToast(t('checkView.messages.deleteSuccess'), 'success');
            fetchVideos();
        } catch (error: any) {
            showToast(error.response?.data?.error || t('checkView.messages.error'), 'error');
        }
    }

    const updateVideo = async (id: number, data: Partial<ViewYoutube>) => {
        try {
            await viewYoutubeService.updateDetail(id, {
                content: data.Content,
                country: data.Country,
                view: data.View,
                nameChannel: data.NameChannel,
                seeder: data.Uploader,
                groupLink: data.Linkvideo,
            });

            if (data.status) {
                await viewYoutubeService.updateStatus(id, data.status);
            }

            showToast(t('checkView.messages.updateSuccess'), 'success');
            fetchVideos();
            return true;
        } catch (error: any) {
            showToast(error.response?.data?.error || t('checkView.messages.error'), 'error');
            return false;
        }
    }

    return {
        youtubeUrl,
        setYoutubeUrl,
        checkViewLoading,
        videoData,
        videos,
        loading,
        search,
        setSearch,
        channelFilter,
        setChannelFilter,
        channelOptions,
        dateStart,
        setDateStart,
        dateEnd,
        setDateEnd,
        statusFilter,
        setStatusFilter,
        page,
        setPage,
        total,
        perPage,
        checkViewEnabled,
        loadingSetting,
        handleCheckView,
        deleteVideo,
        updateVideo,
        refresh: fetchVideos,
        totalPages: Math.ceil(total / perPage)
    };
};
